import React from 'react';
import { stringifyUrl } from 'query-string';
import buildClassNames from 'classnames';

import { ColorContrastProvider, useColorContrastState } from './context';
import { WCAG_CONTRAST_RATIOS } from './constants';
import colorUtils from './utilities';

function ColorContrastInvalidInput() {
    return (
        <div className="txt txt--error">
            This is not a valid hex code. Please enter a six digit hex with a
            hash like so #000000
        </div>
    );
}

function ColorContrastInputs() {
    const {
        fgColor,
        setFgColor,
        bgColor,
        setBgColor,
        swapColors,
        contrastRatio,
    } = useColorContrastState();

    const checkForHashSymbol = (event) => {
        let { value } = event.currentTarget;
        const { id } = event.target;
        let setColor = id === 'fgHex' ? setFgColor : setBgColor;

        if (typeof value !== 'string' || !value.length || value.length < 1) {
            return;
        }

        if (value.startsWith('#')) {
            if (value.length === 4) {
                const withoutHash = value.substring(1);
                const amendedValue = `#${withoutHash}${withoutHash}`;
                return setColor(amendedValue);
            } else {
                return;
            }
        }

        value = value.length === 3 ? value + value : value;
        const prependedValue = `#${value}`;
        return setColor(prependedValue);
    };

    return (
        <div className="tier">
            <div className="grid grid--gutter">
                <div className="grid__col">
                    <form className="vr-array vr-array--2x" noValidate>
                        <fieldset>
                            <legend>Foreground Color</legend>

                            <div className="grid grid--gutter">
                                <div className="grid__col">
                                    <div className="field">
                                        <label
                                            className="field__label"
                                            htmlFor="fgHex">
                                            Hex
                                        </label>
                                        <input
                                            className="field__input"
                                            id="fgHex"
                                            name="fgHex"
                                            type="text"
                                            maxLength="7"
                                            required
                                            value={fgColor}
                                            onChange={(event) =>
                                                setFgColor(
                                                    event.currentTarget.value
                                                )
                                            }
                                            onBlur={(event) => checkForHashSymbol(event)}
                                        />
                                    </div>
                                </div>
                                <div className="grid__col">
                                    <div className="field">
                                        <label
                                            className="field__label"
                                            htmlFor="fgHexPicker">
                                            Pick Color
                                        </label>
                                        <input
                                            className="field__input"
                                            id="fgHexPicker"
                                            name="fgHexPicker"
                                            type="color"
                                            value={fgColor}
                                            onChange={(event) =>
                                                setFgColor(
                                                    event.currentTarget.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {!colorUtils.isValidHexString(fgColor) && (
                                <>
                                    <div className="vr vr--2x" />
                                    <ColorContrastInvalidInput />
                                </>
                            )}
                        </fieldset>

                        <button
                            className="btn"
                            type="button"
                            onClick={(event) => swapColors()}>
                            Swap Colors
                        </button>

                        <fieldset>
                            <legend>Background Color</legend>

                            <div className="grid grid--gutter u-vr--2x">
                                <div className="grid__col">
                                    <div className="field">
                                        <label
                                            className="field__label"
                                            htmlFor="bgHex">
                                            Hex
                                        </label>
                                        <input
                                            className="field__input"
                                            id="bgHex"
                                            name="bgHex"
                                            type="text"
                                            maxLength="7"
                                            required
                                            value={bgColor}
                                            onChange={(event) =>
                                                setBgColor(
                                                    event.currentTarget.value
                                                )
                                            }
                                            onBlur={(event) => checkForHashSymbol(event)}
                                        />
                                    </div>
                                </div>
                                <div className="grid__col">
                                    <div className="field">
                                        <label
                                            className="field__label"
                                            htmlFor="bgHexPicker">
                                            Pick Color
                                        </label>
                                        <input
                                            className="field__input"
                                            id="bgHexPicker"
                                            name="bgHexPicker"
                                            type="color"
                                            value={bgColor}
                                            onChange={(event) =>
                                                setBgColor(
                                                    event.currentTarget.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {!colorUtils.isValidHexString(bgColor) && (
                                <>
                                    <div className="vr vr--2x" />
                                    <ColorContrastInvalidInput />
                                </>
                            )}
                        </fieldset>
                    </form>
                </div>
                <div className="grid__col">
                    <ColorContrastRatioPreview ratio={contrastRatio.n} />
                    <div className="vr vr--2x" />
                    <a
                        className="permalink__anchor"
                        href={stringifyUrl({
                            url: '/',
                            query: { fgColor, bgColor },
                        })}>
                        Permalink
                    </a>
                </div>
            </div>
        </div>
    );
}

function ColorContrastRatioTest({ label, ratio, threshold }) {
    return (
        <div
            className={buildClassNames('contrast-ratio-test', {
                'contrast-ratio-test--pass': ratio >= threshold,
            })}>
            <div>
                {label} {ratio >= threshold ? <>&#10004;</> : <>&#10006;</>}
            </div>
        </div>
    );
}
function AANormalTextColorContrastRatioTest({ ratio }) {
    return (
        <ColorContrastRatioTest
            label="WCAG AA"
            ratio={ratio}
            threshold={WCAG_CONTRAST_RATIOS.AA.NORMAL}
        />
    );
}

function AAANormalTextColorContrastRatioTest({ ratio }) {
    return (
        <ColorContrastRatioTest
            label="WCAG AAA"
            ratio={ratio}
            threshold={WCAG_CONTRAST_RATIOS.AAA.NORMAL}
        />
    );
}

function AALargeTextColorContrastRatioTest({ ratio }) {
    return (
        <ColorContrastRatioTest
            label="WCAG AA"
            ratio={ratio}
            threshold={WCAG_CONTRAST_RATIOS.AA.LARGE}
        />
    );
}

function AAALargeTextColorContrastRatioTest({ ratio }) {
    return (
        <ColorContrastRatioTest
            label="WCAG AAA"
            ratio={ratio}
            threshold={WCAG_CONTRAST_RATIOS.AAA.LARGE}
        />
    );
}

function ColorContrastPreview({ label, fgColor, bgColor }) {
    return (
        <div
            className="contrast-preview"
            style={{
                backgroundColor: bgColor,
                color: fgColor,
            }}>
            <div>{label}</div>
        </div>
    );
}

function ColorContrastRatioPreview(props) {
    const { ratio, threshold = WCAG_CONTRAST_RATIOS.AA.NORMAL } = props;

    return (
        <div
            className={buildClassNames('contrast-ratio-preview', {
                'contrast-ratio-preview--pass': ratio >= threshold,
            })}
        >
            <div>Contrast Ratio</div>
            <div className="hdg hdg--huge">{`${ratio.toFixed(2)}:1`}</div>
        </div>
    );
}

function ColorContrastTests() {
    const {
        fgColor,
        bgColor,
        fgColorDeficiencyMap,
        bgColorDeficiencyMap,
        contrastRatio,
    } = useColorContrastState();

    return (
        <div className="tier">
            <div className="grid grid--gutter">
                <div className="test-text-size-wrapper">
                    <h2>Normal Text</h2>
                    <ColorContrastPreview
                        label="Normal Text"
                        fgColor={fgColor}
                        bgColor={bgColor}
                    />
                    <div className="vr vr--2x" />
                    <AANormalTextColorContrastRatioTest
                        ratio={contrastRatio.n}
                    />
                    <AAANormalTextColorContrastRatioTest
                        ratio={contrastRatio.n}
                    />
                </div>
            </div>
            <div className="grid grid--gutter">
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Protanopia</h3>
                            <ColorContrastPreview
                                label="Normal Text"
                                fgColor={fgColorDeficiencyMap.pr}
                                bgColor={bgColorDeficiencyMap.pr}
                            />
                            <div className="vr vr--2x" />
                            <AANormalTextColorContrastRatioTest
                                ratio={contrastRatio.pr}
                            />
                            <AAANormalTextColorContrastRatioTest
                                ratio={contrastRatio.pr}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Deuteranopia</h3>
                            <ColorContrastPreview
                                label="Normal Text"
                                fgColor={fgColorDeficiencyMap.de}
                                bgColor={bgColorDeficiencyMap.de}
                            />
                            <div className="vr vr--2x" />
                            <AANormalTextColorContrastRatioTest
                                ratio={contrastRatio.de}
                            />
                            <AAANormalTextColorContrastRatioTest
                                ratio={contrastRatio.de}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Tritanopia</h3>
                            <ColorContrastPreview
                                label="Normal Text"
                                fgColor={fgColorDeficiencyMap.tr}
                                bgColor={bgColorDeficiencyMap.tr}
                            />
                            <div className="vr vr--2x" />
                            <AANormalTextColorContrastRatioTest
                                ratio={contrastRatio.tr}
                            />
                            <AAANormalTextColorContrastRatioTest
                                ratio={contrastRatio.tr}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Color Blindness</h3>
                            <ColorContrastPreview
                                label="Normal Text"
                                fgColor={fgColorDeficiencyMap.cb}
                                bgColor={bgColorDeficiencyMap.cb}
                            />
                            <div className="vr vr--2x" />
                            <AANormalTextColorContrastRatioTest
                                ratio={contrastRatio.cb}
                            />
                            <AAANormalTextColorContrastRatioTest
                                ratio={contrastRatio.cb}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid--gutter">
                <div className="test-text-size-wrapper">
                    <h2>Graphics or Large Text</h2>
                    <ColorContrastPreview
                        label="Graphics or Large Text"
                        fgColor={fgColor}
                        bgColor={bgColor}
                    />
                    <div className="vr vr--2x" />
                    <AALargeTextColorContrastRatioTest
                        ratio={contrastRatio.n}
                    />
                    <AAALargeTextColorContrastRatioTest
                        ratio={contrastRatio.n}
                    />
                </div>
            </div>
            <div className="grid grid--gutter">
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Protanopia</h3>
                            <ColorContrastPreview
                                label="Graphics or Large Text"
                                fgColor={fgColorDeficiencyMap.pr}
                                bgColor={bgColorDeficiencyMap.pr}
                            />
                            <div className="vr vr--2x" />
                            <AALargeTextColorContrastRatioTest
                                ratio={contrastRatio.pr}
                            />
                            <AAALargeTextColorContrastRatioTest
                                ratio={contrastRatio.pr}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Deuteranopia</h3>
                            <ColorContrastPreview
                                label="Graphics or Large Text"
                                fgColor={fgColorDeficiencyMap.de}
                                bgColor={bgColorDeficiencyMap.de}
                            />
                            <div className="vr vr--2x" />
                            <AALargeTextColorContrastRatioTest
                                ratio={contrastRatio.de}
                            />
                            <AAALargeTextColorContrastRatioTest
                                ratio={contrastRatio.de}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Tritanopia</h3>
                            <ColorContrastPreview
                                label="Graphics or Large Text"
                                fgColor={fgColorDeficiencyMap.tr}
                                bgColor={bgColorDeficiencyMap.tr}
                            />
                            <div className="vr vr--2x" />
                            <AALargeTextColorContrastRatioTest
                                ratio={contrastRatio.tr}
                            />
                            <AAALargeTextColorContrastRatioTest
                                ratio={contrastRatio.tr}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid__col">
                    <div className="vr-array vr-array--2x">
                        <div>
                            <h3>Color Blindness</h3>
                            <ColorContrastPreview
                                label="Graphics or Large Text"
                                fgColor={fgColorDeficiencyMap.cb}
                                bgColor={bgColorDeficiencyMap.cb}
                            />
                            <div className="vr vr--2x" />
                            <AALargeTextColorContrastRatioTest
                                ratio={contrastRatio.cb}
                            />
                            <AAALargeTextColorContrastRatioTest
                                ratio={contrastRatio.cb}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ColorContrastPage() {
    return (
        <ColorContrastProvider>
            <ColorContrastInputs />
            <ColorContrastTests />
        </ColorContrastProvider>
    );
}

export { ColorContrastPage };
